/**
 * Idea Pad — talks to the Chalice /ideas endpoints.
 * Sends the Cognito ID token as the Bearer for the API Gateway authorizer.
 *
 * Workflow recap (full doc in portal/IDEA_PAD.md):
 *   submitIdea({ rawText }) → backend shapes via Claude and writes a 'raw' row
 *   confirmIdea(ideaId)     → flips the row to 'confirmed'
 *   listIdeas()             → newest-first history for the current parent
 */
// Strip any trailing slash so `${API_BASE}/ideas` never produces `/api//ideas`.
// API Gateway treats the double-slash path as unknown and the resulting 403
// has no CORS headers, which surfaces in the browser as "Failed to fetch".
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string).replace(/\/+$/, '');

export type Pillar = 'Body' | 'Brain' | 'Bonds';
export type Tag =
  | 'Safety / Privacy'
  | 'Operations'
  | 'Events'
  | 'Product Feature'
  | 'Volunteer Support';
export type Priority = 'High' | 'Medium' | 'Low';
export type IdeaStatus = 'raw' | 'confirmed';

export type Idea = {
  cognitoSub: string;
  ideaId: string;
  status: IdeaStatus;
  rawText: string;
  summary: string;
  pillars: Pillar[];
  tags: Tag[];
  priority: Priority;
  proposedAction: string;
  followUp: string;
  offTopic: boolean;
  model: string;
  createdAt: string;
  confirmedAt?: string;
  submittedBy?: string;
};

async function asError(res: Response, fallback: string): Promise<Error> {
  let detail = '';
  try {
    detail = await res.text();
  } catch {
    /* ignore */
  }
  return new Error(`${fallback}: ${res.status} ${detail}`.trim());
}

export async function submitIdea(idToken: string, rawText: string): Promise<Idea> {
  const res = await fetch(`${API_BASE}/ideas`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ rawText }),
  });
  if (!res.ok) throw await asError(res, 'submitIdea failed');
  return res.json();
}

export async function confirmIdea(idToken: string, ideaId: string): Promise<Idea> {
  const res = await fetch(
    `${API_BASE}/ideas/${encodeURIComponent(ideaId)}/confirm`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}` },
    },
  );
  if (!res.ok) throw await asError(res, 'confirmIdea failed');
  return res.json();
}

export async function listIdeas(idToken: string): Promise<Idea[]> {
  const res = await fetch(`${API_BASE}/ideas`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw await asError(res, 'listIdeas failed');
  const data = (await res.json()) as { items: Idea[] };
  return data.items ?? [];
}
